// ═══════════════════════════════════════════════════════════════
// CULTIVATION PIPELINE TAB — Horizontal cultivation board
// Path: src/components/cultivation/CultivationPipelineTab.jsx
// ═══════════════════════════════════════════════════════════════

import React, { useState } from "react";
import { Minimize2, Maximize2 } from "lucide-react";
import { FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { useApp } from "../../context/AppContext";
import {
  ALL_PIPELINE_STAGES,
  getPipelineStageColors,
  getPipelineAction,
} from "../../utils/cultivationUtils";
import { PipelineStageColumn } from "./PipelineStageColumn";
import { PipelineDrawer } from "./PipelineDrawer";
import { PipelineHelpOverlay } from "./PipelineHelpOverlay";

export const CultivationPipelineTab = ({ pipelineBuckets, enriched }) => {
  const { palette, navigate, updateMember, addContactLog, showMessage } =
    useApp();

  const [drawerMember, setDrawerMember] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [collapsedColumns, setCollapsedColumns] = useState({});

  const allCollapsed = ALL_PIPELINE_STAGES.every(
    (s) => collapsedColumns[s.key]
  );
  const toggleColumn = (key) =>
    setCollapsedColumns((prev) => ({ ...prev, [key]: !prev[key] }));
  const collapseAll = () => {
    const next = {};
    ALL_PIPELINE_STAGES.forEach((s) => {
      next[s.key] = true;
    });
    setCollapsedColumns(next);
  };
  const expandAll = () => setCollapsedColumns({});

  const pipelineOverdueCount = enriched.filter(
    (m) => getPipelineAction(m, m.engagement).urgency === "overdue"
  ).length;

  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Pipeline Top Bar */}
      <div
        style={{
          padding: "8px 14px",
          background: `linear-gradient(135deg, ${
            palette.accent1 || "#6d28d9"
          }dd, ${palette.accent1 || "#6d28d9"}99)`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span
            style={{
              fontWeight: 800,
              fontSize: 15,
              color: "#fff",
              fontFamily: FONT_FAMILY,
            }}
          >
            Cultivation Pipeline
          </span>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              opacity: 0.5,
            }}
          >
            {ALL_PIPELINE_STAGES.map((s, i) => (
              <span key={s.key} style={{ fontSize: 10, color: "#fff" }}>
                {s.label}
                {i < ALL_PIPELINE_STAGES.length - 1 ? " → " : ""}
              </span>
            ))}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {pipelineOverdueCount > 0 && (
            <span
              style={{
                background: "#dc2626",
                color: "#fff",
                fontSize: 11,
                fontWeight: 700,
                padding: "3px 10px",
                borderRadius: 12,
              }}
            >
              {pipelineOverdueCount} overdue
            </span>
          )}
          <button
            onClick={allCollapsed ? expandAll : collapseAll}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 5,
              background: "rgba(255,255,255,0.15)",
              border: "1px solid rgba(255,255,255,0.25)",
              color: "#fff",
              fontSize: 11,
              fontWeight: 700,
              cursor: "pointer",
              padding: "4px 10px",
              borderRadius: 12,
              lineHeight: 1,
              transition: "background 0.15s",
              fontFamily: FONT_FAMILY,
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = "rgba(255,255,255,0.3)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "rgba(255,255,255,0.15)")
            }
            title={allCollapsed ? "Expand all columns" : "Collapse all columns"}
          >
            {allCollapsed ? <Maximize2 size={ICON_SIZES.xs} /> : <Minimize2 size={ICON_SIZES.xs} />}
            {allCollapsed ? "Expand All" : "Collapse All"}
          </button>
          <button
            onClick={() => setShowHelp(true)}
            style={{
              background: "rgba(255,255,255,0.15)",
              border: "1px solid rgba(255,255,255,0.25)",
              color: "#fff",
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
              padding: "3px 9px",
              borderRadius: 12,
              lineHeight: 1,
              transition: "background 0.15s",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = "rgba(255,255,255,0.3)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "rgba(255,255,255,0.15)")
            }
            title="How to use this board"
          >
            ?
          </button>
        </div>
      </div>

      {/* The Board: 5 columns */}
      <div
        style={{
          flex: 1,
          display: "flex",
          gap: 6,
          padding: 6,
          justifyContent: "center",
          overflowX: "auto",
          overflowY: "hidden",
          background: `${palette.text3 || "#9ca3af"}10`,
        }}
      >
        {ALL_PIPELINE_STAGES.map((stage) => {
          const colors = getPipelineStageColors(palette)[stage.key];
          return (
            <PipelineStageColumn
              key={stage.key}
              stage={stage}
              stageColors={colors}
              members={pipelineBuckets[stage.key]}
              palette={palette}
              onMemberClick={setDrawerMember}
              collapsed={!!collapsedColumns[stage.key]}
              onToggleCollapse={() => toggleColumn(stage.key)}
            />
          );
        })}
      </div>

      {/* Drawer & Help Overlays */}
      {drawerMember && (
        <PipelineDrawer
          member={drawerMember}
          palette={palette}
          onClose={() => setDrawerMember(null)}
          onOpenProfile={(m) => {
            setDrawerMember(null);
            navigate("memberDetail", m);
          }}
          onUpdateMember={updateMember}
          onAddContactLog={addContactLog}
          onShowMessage={showMessage}
        />
      )}
      {showHelp && (
        <PipelineHelpOverlay
          palette={palette}
          onClose={() => setShowHelp(false)}
        />
      )}

      <style>{`@keyframes pipelineDrawerSlide { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>
    </div>
  );
};
